import { getRecommendation, resolveRecomIdentity } from '@/data/recom';
import type {
	RecommendationResponse,
	RecommendationSizeMode,
	RecommendationSizes,
	RecommendationType,
} from '@/data/recom';
import { useRecomIdentity } from 'integration/hooks/useRecomIdentity';
import useSWR from 'swr';

export const DATA_KEY_RECOM_RECOMMENDATION = 'RECOM_RECOMMENDATION';

export const RECOM_RECOMMENDATION_DEFAULT_LIMIT = 20;

const OPTIONAL_RECOMMENDATION_TYPES: RecommendationType[] = ['sameBrand', 'family'];

const withoutOptionalTypes = (type: RecommendationType[]) =>
	type.filter((item) => !OPTIONAL_RECOMMENDATION_TYPES.includes(item));

type RecomRecommendationKeyArgs = {
	partNumber: string;
	type: RecommendationType[];
	filters: Record<string, unknown>[];
	limit: number;
	userId?: string | null;
	sizes?: RecommendationSizes;
	sizeMode?: RecommendationSizeMode;
	partNumbers?: string[];
	withoutIdentity?: boolean;
};

// El sessionId de invitado se guarda en localStorage y se genera en el
// navegador, asi que el servidor no puede reproducirlo. Queda fuera de la llave
// (el fetcher si lo manda) para que el prefetch del SSR empate y el carrusel no
// salga como esqueleto en el HTML servido.
export const recomRecommendationKey = ({
	partNumber,
	type,
	filters,
	limit,
	userId,
	sizes,
	sizeMode,
	partNumbers,
	withoutIdentity,
}: RecomRecommendationKeyArgs) =>
	[
		{
			partNumber,
			type,
			filters,
			limit,
			userId,
			...(sizes ? { sizes } : {}),
			...(sizeMode ? { sizeMode } : {}),
			...(partNumbers?.length ? { partNumbers } : {}),
			...(withoutIdentity ? { withoutIdentity } : {}),
		},
		DATA_KEY_RECOM_RECOMMENDATION,
	] as const;

export const useGetRecomRecommendation = ({
	partNumber,
	type,
	filters = [],
	limit = RECOM_RECOMMENDATION_DEFAULT_LIMIT,
	sizes,
	sizeMode,
	partNumbers,
	withoutIdentity,
}: {
	partNumber?: string;
	type: RecommendationType[];
	filters?: Record<string, unknown>[];
	limit?: number;
	sizes?: RecommendationSizes;
	sizeMode?: RecommendationSizeMode;
	partNumbers?: string[];
	withoutIdentity?: boolean;
}) => {
	const { identity, isLoadingIdentity } = useRecomIdentity();
	const { userId, sessionId } = resolveRecomIdentity(withoutIdentity ? {} : identity);

	return useSWR(
		partNumber && (withoutIdentity || !isLoadingIdentity)
			? recomRecommendationKey({
					partNumber,
					type,
					filters,
					limit,
					userId,
					sizes,
					sizeMode,
					partNumbers,
					withoutIdentity,
			  })
			: null,
		async ([props]): Promise<RecommendationResponse | undefined> => {
			const request = (requestType: RecommendationType[]) =>
				getRecommendation({
					type: requestType,
					params: {
						partnumber: props.partNumber,
						limit: props.limit,
						...(props.userId ? { userId: props.userId } : {}),
						...(sessionId ? { sessionId } : {}),
						...(props.sizes ? { sizes: props.sizes } : {}),
						...(props.sizeMode ? { sizeMode: props.sizeMode } : {}),
						...(props.partNumbers ? { partnumbers: props.partNumbers } : {}),
					},
					filters: { values: props.filters },
				});
			const fallbackType = withoutOptionalTypes(props.type);

			try {
				return await request(props.type);
			} catch {
				return fallbackType.length < props.type.length
					? request(fallbackType).catch(() => undefined)
					: undefined;
			}
		}
	);
};
